import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowRight, FiCalendar, FiCreditCard, FiUploadCloud, FiUser } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { hotelService } from '../services/hotelService';
import { paymentService } from '../services/paymentService';
import { reservationService } from '../services/reservationService';
import { ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';
import { calculateNights, formatCurrency } from '../utils/formatters';

const BANK_IBAN = 'IR16 0160 0000 1113 8445 32';
const BANK_NAME = 'Keshavarzi';
const BANK_ACCOUNT_NAME = 'Arslan Shiri Dudkanlu';

const ReservationPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [hotel, setHotel] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [createdReservation, setCreatedReservation] = useState(null);
  const [createdPayment, setCreatedPayment] = useState(null);
  const [showPaymentInfo, setShowPaymentInfo] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    checkInDate: '',
    checkOutDate: '',
    rooms: [{ type: '', quantity: 1, pricePerNight: 0 }],
    paymentMethod: 'bank',
    specialRequests: ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [hotelData, roomTypeData] = await Promise.all([
          hotelService.getHotelById(id),
          hotelService.getRoomTypesByHotelId(id).catch(() => [])
        ]);
        setHotel(hotelData);
        setRoomTypes(roomTypeData || []);
        setError('');
      } catch (requestError) {
        console.error('Reservation page error:', requestError);
        setError(t('design.hotelReservation.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, t]);

  const nights = useMemo(() => calculateNights(formData.checkInDate, formData.checkOutDate), [formData.checkInDate, formData.checkOutDate]);
  const totalAmount = useMemo(
    () =>
      formData.rooms.reduce((sum, room) => {
        return sum + Number(room.pricePerNight || 0) * Number(room.quantity || 0) * nights;
      }, 0),
    [formData.rooms, nights]
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleRoomChange = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      rooms: current.rooms.map((room, roomIndex) => {
        if (roomIndex !== index) return room;
        const updatedRoom = { ...room, [field]: value };
        if (field === 'type') {
          const selectedRoomType = roomTypes.find((roomType) => String(roomType.id) === String(value));
          updatedRoom.pricePerNight = selectedRoomType ? selectedRoomType.pricePerNight : 0;
        }
        return updatedRoom;
      })
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (!hotel?.id) {
        throw new Error(t('design.hotelReservation.errors.hotelMissing'));
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkIn = new Date(formData.checkInDate);

      if (checkIn < today) throw new Error(t('design.hotelReservation.errors.checkInPast'));
      if (nights <= 0) throw new Error(t('design.hotelReservation.errors.checkOutInvalid'));

      const reservationData = {
        hotelId: hotel.id,
        status: 'PENDING',
        totalAmount,
        checkIn: formData.checkInDate,
        checkOut: formData.checkOutDate,
        reservationCreatedAt: new Date().toISOString().split('T')[0],
        reservationUpdatedAt: new Date().toISOString().split('T')[0],
        guestName: formData.fullName,
        guestEmail: formData.email,
        guestPhone: formData.phone,
        specialRequests: formData.specialRequests || '',
        roomType: formData.rooms[0].type,
        roomCount: parseInt(formData.rooms[0].quantity, 10),
        pricePerNight: formData.rooms[0].pricePerNight
      };

      const reservationResponse = await reservationService.createReservation(reservationData);
      setCreatedReservation(reservationResponse);

      if (formData.paymentMethod === 'bank') {
        const paymentData = {
          reservationId: reservationResponse.id,
          amount: totalAmount,
          currency: 'TRY',
          status: 'PENDING',
          provider: 'BANK_TRANSFER',
          transactionId: `BT-${reservationResponse.pnrCode}`,
          paymentDate: new Date().toISOString()
        };

        try {
          const paymentResponse = await paymentService.createPayment(paymentData);
          setCreatedPayment(paymentResponse);
        } catch (paymentError) {
          console.error('Payment create error:', paymentError);
        }

        setShowPaymentInfo(true);
      } else {
        navigate('/');
      }
    } catch (submitError) {
      console.error('Reservation submit error:', submitError);
      alert(submitError.message || t('design.hotelReservation.errors.submitFailed'));
    }
  };

  const handleReceiptUpload = async () => {
    if (!receiptFile || !createdPayment) {
      alert(t('design.hotelReservation.errors.selectReceiptFirst'));
      return;
    }

    try {
      setUploadingReceipt(true);

      if (receiptFile.size > 5 * 1024 * 1024) {
        throw new Error(t('design.hotelReservation.errors.fileTooLarge'));
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(receiptFile.type)) {
        throw new Error(t('design.hotelReservation.errors.invalidFileType'));
      }

      const result = await paymentService.uploadReceipt(receiptFile, createdPayment.id);

      if (!result) {
        throw new Error(t('design.hotelReservation.errors.receiptIncomplete'));
      }

      navigate('/reservation-confirmation', {
        state: {
          reservationDetails: {
            ...createdReservation,
            hotelName: hotel?.hotelName
          }
        }
      });
    } catch (uploadError) {
      console.error('Receipt upload error:', uploadError);

      if (uploadError.response?.status === 403) {
        navigate('/reservation-confirmation', {
          state: {
            reservationDetails: {
              ...createdReservation,
              hotelName: hotel?.hotelName
            }
          }
        });
        return;
      }

      alert(uploadError.message || t('design.hotelReservation.errors.receiptFailed'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.hotelReservation.loading')} />
      </Layout>
    );
  }

  if (error || !hotel) {
    return (
      <Layout>
        <ErrorState title={t('design.hotelReservation.unavailableTitle')} message={error || t('design.hotelReservation.unavailableMessage')} action={<Link to="/hotels" className="btn-primary">{t('design.hotelReservation.backToHotels')}</Link>} />
      </Layout>
    );
  }

  if (showPaymentInfo) {
    return (
      <Layout>
        <PageHero
          eyebrow={t('design.hotelReservation.transferHeroEyebrow')}
          title={t('design.hotelReservation.transferHeroTitle')}
          description={t('design.hotelReservation.transferHeroDescription')}
          aside={
            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">{t('design.hotelReservation.pnrReference')}</p>
              <p className="mt-5 font-serif text-4xl font-semibold text-slate-900">{createdReservation?.pnrCode}</p>
              <p className="mt-3 text-sm leading-7 text-slate-600">{t('design.hotelReservation.pnrReferenceNote')}</p>
            </div>
          }
        />

        <section className="content-width pb-12">
          <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
            <div className="soft-panel p-6 sm:p-8">
              <SectionHeading eyebrow={t('hotels.reservation.bankDetails')} title={t('design.hotelReservation.transferInfoTitle')} description={t('design.hotelReservation.transferInfoDescription')} />
              <div className="grid gap-4">
                {[
                  [t('hotels.reservation.bank'), BANK_NAME],
                  [t('hotels.reservation.accountHolder'), BANK_ACCOUNT_NAME],
                  ['IBAN', BANK_IBAN],
                  [t('hotels.reservation.totalAmount'), formatCurrency(createdReservation?.totalAmount)],
                  [t('hotels.reservation.description'), createdReservation?.pnrCode]
                ].map(([label, value]) => (
                  <div key={label} className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</p>
                    <p className="mt-2 text-base font-semibold text-slate-900">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="soft-panel p-6 sm:p-8">
                <SectionHeading eyebrow={t('hotels.reservation.uploadReceipt')} title={t('design.hotelReservation.uploadTitle')} description={t('design.hotelReservation.uploadDescription')} />
                <div className="rounded-[28px] border border-dashed border-stone-300 bg-stone-50/80 p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(event) => setReceiptFile(event.target.files?.[0] || null)}
                  />
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-700">
                    <FiUploadCloud className="h-8 w-8" />
                  </div>
                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    {receiptFile ? t('design.hotelReservation.fileSelected', { name: receiptFile.name }) : t('design.hotelReservation.uploadHint')}
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-3">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-ghost">{t('design.hotelReservation.chooseFile')}</button>
                    {receiptFile ? (
                      <button type="button" onClick={handleReceiptUpload} className="btn-primary" disabled={uploadingReceipt}>
                        {uploadingReceipt ? t('hotels.reservation.uploading') : t('design.hotelReservation.uploadReceiptButton')}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="soft-panel p-6 sm:p-8">
                <p className="eyebrow">{t('hotels.reservation.attention')}</p>
                <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-600">
                  <li>{t('design.hotelReservation.notes.transferNote')}</li>
                  <li>{t('design.hotelReservation.notes.uploadQuickly')}</li>
                  <li>{t('design.hotelReservation.notes.supportHelp')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.hotelReservation.heroEyebrow')}
        title={t('design.hotelReservation.heroTitle', { name: hotel.hotelName })}
        description={t('design.hotelReservation.heroDescription')}
        aside={
          <div className="soft-panel p-6 sm:p-8">
            <p className="eyebrow">{t('design.hotelReservation.summaryEyebrow')}</p>
            <div className="mt-6 space-y-4">
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelReservation.rate')}</p>
                <p className="mt-2 font-serif text-3xl font-semibold text-slate-900">{formatCurrency(formData.rooms[0].pricePerNight || hotel.advancePayment)}</p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelReservation.nights')}</p>
                <p className="mt-2 text-xl font-semibold text-slate-900">{nights || 0}</p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelReservation.estimatedTotal')}</p>
                <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="soft-panel p-6 sm:p-8">
            <SectionHeading eyebrow={t('design.hotelReservation.formEyebrow')} title={t('design.hotelReservation.formTitle')} description={t('design.hotelReservation.formDescription')} />

            <form onSubmit={handleSubmit} className="grid gap-5">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="field-label">{t('hotels.reservation.fullName')}</label>
                  <input name="fullName" value={formData.fullName} onChange={handleInputChange} required className="input-shell" />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.email')}</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="input-shell" />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.phone')}</label>
                  <input name="phone" value={formData.phone} onChange={handleInputChange} required className="input-shell" />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.checkIn')}</label>
                  <input type="date" name="checkInDate" value={formData.checkInDate} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} required className="input-shell" />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.checkOut')}</label>
                  <input type="date" name="checkOutDate" value={formData.checkOutDate} onChange={handleInputChange} min={formData.checkInDate || new Date().toISOString().split('T')[0]} required className="input-shell" />
                </div>
                <div>
                  <label className="field-label">{t('hotels.reservation.paymentMethod')}</label>
                  <div className="input-shell flex items-center gap-3">
                    <FiCreditCard className="text-teal-700" />
                    <span>{t('hotels.reservation.bankTransfer')}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-[28px] border border-stone-200 bg-stone-50/80 p-5">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <FiCalendar className="text-teal-700" />
                  {t('design.hotelReservation.roomSelection')}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-[1fr_120px]">
                  <select
                    value={formData.rooms[0].type}
                    onChange={(event) => handleRoomChange(0, 'type', event.target.value)}
                    className="input-shell"
                    required
                  >
                    <option value="">{t('hotels.reservation.selectRoom')}</option>
                    {roomTypes.length ? (
                      roomTypes.map((roomType) => (
                        <option key={roomType.id} value={roomType.id}>
                          {roomType.name} - {formatCurrency(roomType.pricePerNight)}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>{t('design.hotelReservation.noRoomTypes')}</option>
                    )}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={formData.rooms[0].quantity}
                    onChange={(event) => handleRoomChange(0, 'quantity', parseInt(event.target.value, 10))}
                    className="input-shell"
                    placeholder={t('hotels.reservation.roomQuantity')}
                  />
                </div>
              </div>

              <div>
                <label className="field-label">{t('hotels.reservation.specialRequests')}</label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  rows="5"
                  className="input-shell"
                  placeholder={t('design.hotelReservation.specialRequestsPlaceholder')}
                />
              </div>

              <div className="flex flex-col gap-4 rounded-[28px] border border-stone-200 bg-stone-50/80 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelReservation.reservationTotal')}</p>
                  <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{formatCurrency(totalAmount)}</p>
                </div>
                <button type="submit" className="btn-primary">
                  {t('hotels.reservation.completeReservation')}
                  <FiArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          <div className="space-y-6">
            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">Flow note</p>
              <div className="mt-5 flex items-start gap-3 rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <FiUser className="mt-1 text-teal-700" />
                <p className="text-sm leading-7 text-slate-600">
                  The updated checkout makes guest details, stay information and payment expectations much easier to parse at a glance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReservationPage;
